import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Inventory as InventoryIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';

const WholesaleSuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [addSupplierDialogOpen, setAddSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [newSupplier, setNewSupplier] = useState({
    supplierCode: '',
    companyName: '',
    website: '',
    contact: {
      name: '',
      email: '',
      phone: ''
    },
    paymentTerms: '',
    apiIntegration: {
      enabled: false,
      apiType: '',
      apiUrl: '',
      apiKey: '',
      apiSecret: '',
      authType: 'api_key',
      syncSchedule: {
        enabled: false,
        frequency: 'daily'
      }
    }
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/wholesale-suppliers');
      if (response.data.success) {
        setSuppliers(response.data.suppliers);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSupplier = async (supplierId) => {
    try {
      const response = await axios.get(`/api/wholesale-suppliers/${supplierId}`);
      if (response.data.success) {
        setSelectedSupplier(response.data.supplier);
        setDialogOpen(true);
      }
    } catch (err) {
      console.error('Error fetching supplier details:', err);
      setError('Failed to load supplier details');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSupplier(null);
    setActiveTab(0);
  };

  const handleOpenAddSupplierDialog = () => {
    setEditingSupplier(null);
    setNewSupplier({
      supplierCode: '',
      companyName: '',
      website: '',
      contact: {
        name: '',
        email: '',
        phone: ''
      },
      paymentTerms: '',
      apiIntegration: {
        enabled: false,
        apiType: '',
        apiUrl: '',
        apiKey: '',
        apiSecret: '',
        authType: 'api_key',
        syncSchedule: {
          enabled: false,
          frequency: 'daily'
        }
      }
    });
    setAddSupplierDialogOpen(true);
  };

  const handleOpenEditSupplierDialog = (supplier) => {
    setEditingSupplier(supplier);
    setNewSupplier({
      supplierCode: supplier.supplierCode,
      companyName: supplier.companyName,
      website: supplier.website || '',
      contact: {
        name: supplier.contact?.name || '',
        email: supplier.contact?.email || '',
        phone: supplier.contact?.phone || ''
      },
      paymentTerms: supplier.paymentTerms || '',
      apiIntegration: {
        enabled: supplier.apiIntegration?.enabled || false,
        apiType: supplier.apiIntegration?.apiType || '',
        apiUrl: supplier.apiIntegration?.apiUrl || '',
        apiKey: supplier.apiIntegration?.apiKey || '',
        apiSecret: supplier.apiIntegration?.apiSecret || '',
        authType: supplier.apiIntegration?.authType || 'api_key',
        syncSchedule: {
          enabled: supplier.apiIntegration?.syncSchedule?.enabled || false,
          frequency: supplier.apiIntegration?.syncSchedule?.frequency || 'daily'
        }
      }
    });
    setAddSupplierDialogOpen(true);
  };

  const handleCloseAddSupplierDialog = () => {
    setAddSupplierDialogOpen(false);
    setEditingSupplier(null);
  };

  const handleSupplierInputChange = (field, value) => {
    if (field.startsWith('contact.')) {
      const contactField = field.split('.')[1];
      setNewSupplier(prev => ({
        ...prev,
        contact: {
          ...prev.contact,
          [contactField]: value
        }
      }));
    } else if (field.startsWith('apiIntegration.')) {
      const apiField = field.replace('apiIntegration.', '');
      if (apiField.startsWith('syncSchedule.')) {
        const syncField = apiField.split('.')[1];
        setNewSupplier(prev => ({
          ...prev,
          apiIntegration: {
            ...prev.apiIntegration,
            syncSchedule: {
              ...prev.apiIntegration.syncSchedule,
              [syncField]: value
            }
          }
        }));
      } else {
        setNewSupplier(prev => ({
          ...prev,
          apiIntegration: {
            ...prev.apiIntegration,
            [apiField]: value
          }
        }));
      }
    } else {
      setNewSupplier(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleCreateSupplier = async () => {
    try {
      if (editingSupplier) {
        // Update existing supplier
        const response = await axios.put(`/api/wholesale-suppliers/${editingSupplier.id}`, newSupplier);
        if (response.data.success) {
          setError(null);
          setAddSupplierDialogOpen(false);
          setEditingSupplier(null);
          fetchSuppliers(); // Refresh the list
        }
      } else {
        // Create new supplier
        const response = await axios.post('/api/wholesale-suppliers', newSupplier);
        if (response.data.success) {
          setError(null);
          setAddSupplierDialogOpen(false);
          fetchSuppliers(); // Refresh the list
        }
      }
    } catch (err) {
      console.error('Error saving supplier:', err);
      setError(err.response?.data?.message || 'Failed to save supplier');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Wholesale Suppliers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddSupplierDialog}
        >
          Add Supplier
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Company Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Catalog Items</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>On-Time Delivery</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Quality Score</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Loading...</TableCell>
              </TableRow>
            ) : suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No suppliers found. Add your first wholesale supplier to get started.
                </TableCell>
              </TableRow>
            ) : (
              suppliers.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell>{supplier.supplierCode}</TableCell>
                  <TableCell>
                    <Typography variant="body1">{supplier.companyName}</Typography>
                    {supplier.website && (
                      <Typography variant="caption" color="text.secondary">
                        {supplier.website}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<InventoryIcon />}
                      label={`${supplier.catalogItemCount} items`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {supplier.performance?.onTimeDeliveryRate
                      ? `${(supplier.performance.onTimeDeliveryRate * 100).toFixed(0)}%`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {supplier.performance?.qualityScore
                      ? `${supplier.performance.qualityScore.toFixed(1)}/5`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.status}
                      color={supplier.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewSupplier(supplier.id)}
                      title="View Details"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditSupplierDialog(supplier)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      title="Delete"
                      onClick={async () => {
                        const confirmed = window.confirm(
                          'Are you sure you want to delete or deactivate this supplier?'
                        );
                        if (!confirmed) return;
                        try {
                          const response = await axios.delete(`/api/wholesale-suppliers/${supplier.id}`);
                          if (response.data?.success) {
                            fetchSuppliers();
                          }
                        } catch (err) {
                          console.error('Error deleting supplier:', err);
                          setError(err.response?.data?.error || 'Failed to delete supplier');
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Supplier Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        {selectedSupplier && (
          <>
            <DialogTitle>
              {selectedSupplier.companyName}
              <Typography variant="caption" display="block" color="text.secondary">
                {selectedSupplier.supplierCode}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
                <Tab label="Details" />
                <Tab label={`Catalog (${selectedSupplier.catalog?.length || 0})`} />
                <Tab label="Performance" />
                <Tab label="API Integration" />
              </Tabs>

              {/* Details Tab */}
              {activeTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Company Info</Typography>
                    <Typography variant="body1">{selectedSupplier.companyName}</Typography>
                    {selectedSupplier.website && (
                      <Typography variant="body2" color="primary">
                        <a href={selectedSupplier.website} target="_blank" rel="noopener noreferrer">
                          {selectedSupplier.website}
                        </a>
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
                    {selectedSupplier.contact?.name && (
                      <Typography variant="body2">{selectedSupplier.contact.name}</Typography>
                    )}
                    {selectedSupplier.contact?.email && (
                      <Typography variant="body2">{selectedSupplier.contact.email}</Typography>
                    )}
                    {selectedSupplier.contact?.phone && (
                      <Typography variant="body2">{selectedSupplier.contact.phone}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Payment Terms</Typography>
                    <Typography variant="body1">
                      {selectedSupplier.paymentTerms || 'Not specified'}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {/* Catalog Tab */}
              {activeTab === 1 && (
                <SupplierCatalogView catalog={selectedSupplier.catalog || []} />
              )}

              {/* Performance Tab */}
              {activeTab === 2 && (
                <SupplierPerformanceView performance={selectedSupplier.performance || {}} />
              )}

              {/* API Integration Tab */}
              {activeTab === 3 && (
                <ApiIntegrationView
                  supplier={selectedSupplier}
                  onSync={() => {
                    handleCloseDialog();
                    fetchSuppliers();
                  }}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add Supplier Dialog */}
      <Dialog
        open={addSupplierDialogOpen}
        onClose={handleCloseAddSupplierDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSupplier ? 'Edit Wholesale Supplier' : 'Add New Wholesale Supplier'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* Supplier Code */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier Code"
                required
                value={newSupplier.supplierCode}
                onChange={(e) => handleSupplierInputChange('supplierCode', e.target.value)}
                helperText="Unique identifier for this supplier"
              />
            </Grid>

            {/* Company Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                required
                value={newSupplier.companyName}
                onChange={(e) => handleSupplierInputChange('companyName', e.target.value)}
              />
            </Grid>

            {/* Website */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={newSupplier.website}
                onChange={(e) => handleSupplierInputChange('website', e.target.value)}
                placeholder="https://supplier.com"
              />
            </Grid>

            {/* Contact Name */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Name"
                value={newSupplier.contact.name}
                onChange={(e) => handleSupplierInputChange('contact.name', e.target.value)}
              />
            </Grid>

            {/* Contact Email */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={newSupplier.contact.email}
                onChange={(e) => handleSupplierInputChange('contact.email', e.target.value)}
              />
            </Grid>

            {/* Contact Phone */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={newSupplier.contact.phone}
                onChange={(e) => handleSupplierInputChange('contact.phone', e.target.value)}
              />
            </Grid>

            {/* Payment Terms */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Terms"
                multiline
                rows={2}
                value={newSupplier.paymentTerms}
                onChange={(e) => handleSupplierInputChange('paymentTerms', e.target.value)}
                placeholder="e.g., Net 30, 50% deposit"
              />
            </Grid>

            {/* API Integration Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                API Integration (Optional)
              </Typography>
            </Grid>

            {/* Enable API Integration */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label={newSupplier.apiIntegration.enabled ? 'Enabled' : 'Disabled'}
                    color={newSupplier.apiIntegration.enabled ? 'success' : 'default'}
                    size="small"
                    onClick={() => handleSupplierInputChange('apiIntegration.enabled', !newSupplier.apiIntegration.enabled)}
                    sx={{ cursor: 'pointer', mr: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Enable API integration for automatic catalog syncing
                  </Typography>
                </Box>
              </FormControl>
            </Grid>

            {newSupplier.apiIntegration.enabled && (
              <>
                {/* API Type */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>API Type</InputLabel>
                    <Select
                      value={newSupplier.apiIntegration.apiType}
                      onChange={(e) => handleSupplierInputChange('apiIntegration.apiType', e.target.value)}
                      label="API Type"
                    >
                      <MenuItem value="ssactivewear">S&S Activewear</MenuItem>
                      <MenuItem value="ascolour">AS Colour (CSV)</MenuItem>
                      <MenuItem value="generic_rest">Generic REST API</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* API URL */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="API URL"
                    value={newSupplier.apiIntegration.apiUrl}
                    onChange={(e) => handleSupplierInputChange('apiIntegration.apiUrl', e.target.value)}
                    placeholder="https://api.supplier.com"
                  />
                </Grid>

                {/* API Key */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="API Key / Username"
                    value={newSupplier.apiIntegration.apiKey}
                    onChange={(e) => handleSupplierInputChange('apiIntegration.apiKey', e.target.value)}
                    placeholder="Your API key or username"
                  />
                </Grid>

                {/* API Secret */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="API Secret / Password"
                    type="password"
                    value={newSupplier.apiIntegration.apiSecret}
                    onChange={(e) => handleSupplierInputChange('apiIntegration.apiSecret', e.target.value)}
                    placeholder="Your API secret or password"
                  />
                </Grid>

                {/* Auth Type */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Authentication Type</InputLabel>
                    <Select
                      value={newSupplier.apiIntegration.authType}
                      onChange={(e) => handleSupplierInputChange('apiIntegration.authType', e.target.value)}
                      label="Authentication Type"
                    >
                      <MenuItem value="api_key">API Key</MenuItem>
                      <MenuItem value="basic_auth">Basic Auth</MenuItem>
                      <MenuItem value="bearer_token">Bearer Token</MenuItem>
                      <MenuItem value="oauth">OAuth</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Sync Frequency */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sync Frequency</InputLabel>
                    <Select
                      value={newSupplier.apiIntegration.syncSchedule.frequency}
                      onChange={(e) => handleSupplierInputChange('apiIntegration.syncSchedule.frequency', e.target.value)}
                      label="Sync Frequency"
                    >
                      <MenuItem value="hourly">Hourly</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    After saving, go to the "API Integration" tab to test the connection and sync the catalog.
                  </Alert>
                </Grid>
              </>
            )}

            {!newSupplier.apiIntegration.enabled && (
              <Grid item xs={12}>
                <Alert severity="info">
                  After creating the supplier, you can add catalog items manually or enable API integration later.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddSupplierDialog}>Cancel</Button>
          <Button
            onClick={handleCreateSupplier}
            variant="contained"
            disabled={!newSupplier.supplierCode || !newSupplier.companyName}
          >
            {editingSupplier ? 'Save Changes' : 'Create Supplier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Catalog View Component
const SupplierCatalogView = ({ catalog }) => {
  if (!catalog || catalog.length === 0) {
    return (
      <Alert severity="info">
        No catalog items yet. Add items to this supplier's catalog.
      </Alert>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Price Tiers</TableCell>
            <TableCell>MOQ</TableCell>
            <TableCell>Lead Time</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {catalog.map((item) => (
            <TableRow key={item._id}>
              <TableCell>{item.catalogItemId}</TableCell>
              <TableCell>
                <Typography variant="body2">{item.itemName}</Typography>
                {item.brand && (
                  <Typography variant="caption" color="text.secondary">{item.brand}</Typography>
                )}
              </TableCell>
              <TableCell>{item.category || '-'}</TableCell>
              <TableCell>
                {item.costTiers?.length > 0 ? (
                  <Box>
                    {item.costTiers.map((tier, idx) => (
                      <Typography key={idx} variant="caption" display="block">
                        {tier.minQuantity}+: ${tier.unitCost.toFixed(2)}
                      </Typography>
                    ))}
                  </Box>
                ) : '-'}
              </TableCell>
              <TableCell>{item.moq || 1}</TableCell>
              <TableCell>{item.leadTimeDays || '-'} days</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Performance View Component
const SupplierPerformanceView = ({ performance }) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4">
            {performance.onTimeDeliveryRate
              ? `${(performance.onTimeDeliveryRate * 100).toFixed(0)}%`
              : '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            On-Time Delivery Rate
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4">
            {performance.qualityScore
              ? `${performance.qualityScore.toFixed(1)}/5`
              : '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Quality Score
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h4">
            {performance.orderCount || 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total Orders
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
};

// API Integration View Component
const ApiIntegrationView = ({ supplier, onSync }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);

  useEffect(() => {
    if (supplier?.apiIntegration?.enabled) {
      fetchSyncStatus();
    }
  }, [supplier]);

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`/api/wholesale-suppliers/${supplier._id}/sync-status`);
      if (response.data.success) {
        setSyncStatus(response.data.status);
      }
    } catch (err) {
      console.error('Error fetching sync status:', err);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionResult(null);

    try {
      const response = await axios.post(`/api/wholesale-suppliers/${supplier._id}/test-connection`);
      setConnectionResult(response.data);
    } catch (err) {
      setConnectionResult({
        success: false,
        message: err.response?.data?.error || 'Connection test failed'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);

    try {
      const response = await axios.post(`/api/wholesale-suppliers/${supplier._id}/sync`);
      if (response.data.success) {
        await fetchSyncStatus();
        if (onSync) onSync();
      }
    } catch (err) {
      console.error('Error syncing catalog:', err);
    } finally {
      setSyncing(false);
    }
  };

  const isApiEnabled = supplier?.apiIntegration?.enabled;
  const apiType = supplier?.apiIntegration?.apiType;

  return (
    <Box>
      {!isApiEnabled ? (
        <Alert severity="info">
          API integration is not enabled for this supplier. Configure API settings to enable automatic catalog synchronization.
        </Alert>
      ) : (
        <>
          {/* API Configuration Summary */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">API Type</Typography>
                <Chip
                  label={apiType}
                  color="primary"
                  size="small"
                  sx={{ mt: 0.5, textTransform: 'uppercase' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">API URL</Typography>
                <Typography variant="body2">{supplier.apiIntegration.apiUrl || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Authentication</Typography>
                <Typography variant="body2">{supplier.apiIntegration.authType || 'api_key'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">API Key</Typography>
                <Typography variant="body2">
                  {supplier.apiIntegration.apiKey ? '••••••••' : 'Not configured'}
                </Typography>
              </Grid>
            </Grid>

            {/* Test Connection Button */}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleTestConnection}
                disabled={testingConnection}
                size="small"
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </Button>

              {connectionResult && (
                <Alert
                  severity={connectionResult.success ? 'success' : 'error'}
                  sx={{ mt: 2 }}
                >
                  {connectionResult.message}
                </Alert>
              )}
            </Box>
          </Paper>

          {/* Sync Configuration */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Sync Configuration</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Scheduled Sync</Typography>
                <Typography variant="body2">
                  {supplier.apiIntegration.syncSchedule?.enabled ? 'Enabled' : 'Disabled'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Frequency</Typography>
                <Typography variant="body2">
                  {supplier.apiIntegration.syncSchedule?.frequency || 'Not set'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Auto-Create Items</Typography>
                <Typography variant="body2">
                  {supplier.apiIntegration.syncConfig?.createNewItems ? 'Yes' : 'No'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Sync Status */}
          {syncStatus && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Sync Status</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Last Sync</Typography>
                  <Typography variant="body2">
                    {syncStatus.lastSyncAt
                      ? new Date(syncStatus.lastSyncAt).toLocaleString()
                      : 'Never'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    label={syncStatus.lastSyncStatus || 'never_synced'}
                    color={
                      syncStatus.lastSyncStatus === 'success' ? 'success' :
                      syncStatus.lastSyncStatus === 'error' ? 'error' :
                      syncStatus.lastSyncStatus === 'in_progress' ? 'warning' : 'default'
                    }
                    size="small"
                  />
                </Grid>
                {syncStatus.lastSyncError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{syncStatus.lastSyncError}</Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Next Sync</Typography>
                  <Typography variant="body2">
                    {syncStatus.nextSyncAt
                      ? new Date(syncStatus.nextSyncAt).toLocaleString()
                      : 'Not scheduled'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Catalog Items</Typography>
                  <Typography variant="body2">{syncStatus.catalogItemCount || 0} items</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Manual Sync Button */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
            {syncing && (
              <Alert severity="info" sx={{ flex: 1 }}>
                Syncing catalog from {apiType}... This may take a few moments.
              </Alert>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WholesaleSuppliersPage;
